import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { LayoutDashboard, BookOpen, PlusCircle, Image, FileText, BarChart3, Settings, LogOut, Menu, X, ExternalLink } from 'lucide-react';
import { api } from './api';

const NAV = [
  { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/admin/rezepte', label: 'Rezepte', icon: BookOpen },
  { to: '/admin/rezepte/neu', label: 'Neues Rezept', icon: PlusCircle },
  { to: '/admin/medien', label: 'Medien', icon: Image },
  { to: '/admin/seiten', label: 'Seiten', icon: FileText },
  { to: '/admin/statistik', label: 'Statistik', icon: BarChart3 },
  { to: '/admin/einstellungen', label: 'Einstellungen', icon: Settings },
];

export function AdminLayout({ user, children }) {
  const [open, setOpen] = useState(false);

  const logout = async () => {
    try {
      const res = await api('/auth/logout', { method: 'POST' });
      window.location.assign(res.logout_url || '/admin');
    } catch {
      window.location.assign('/admin');
    }
  };

  return (
    <div className="min-h-screen bg-brand-beige">
      <Helmet>
        <title>Admin | Felix Genuss</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <a href="#admin-main" className="skip-link">Zum Admin-Inhalt springen</a>
      <header className="sticky top-0 z-40 border-b border-brand-line bg-brand-brown text-brand-cream">
        <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <Link to="/admin/dashboard" className="flex items-center gap-2 rounded-sm" data-testid="admin-logo">
            <img src="/assets/brand/logo-256.webp" alt="" width="36" height="36" className="h-9 w-9 rounded-full" />
            <span className="font-serif text-lg font-bold">Felix Genuss <span className="text-brand-gold">Admin</span></span>
          </Link>
          <div className="flex items-center gap-2">
            <span className="hidden text-sm text-brand-cream/70 md:inline">{user.email}</span>
            <a href="/" className="hidden min-h-11 items-center gap-1.5 rounded-sm px-3 py-2 text-sm font-semibold transition-colors hover:text-white sm:inline-flex" data-testid="admin-view-site">
              <ExternalLink className="h-4 w-4" aria-hidden="true" /> Website
            </a>
            <button type="button" onClick={logout} className="inline-flex min-h-11 items-center gap-1.5 rounded-sm bg-brand-olive px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-olive-dark" data-testid="admin-logout-button">
              <LogOut className="h-4 w-4" aria-hidden="true" /> Abmelden
            </button>
            <button
              type="button"
              className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-sm border border-brand-cream/30 lg:hidden"
              aria-expanded={open}
              aria-controls="admin-mobile-nav"
              onClick={() => setOpen((v) => !v)}
              data-testid="admin-menu-toggle"
            >
              {open ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
              <span className="sr-only">{open ? 'Menü schliessen' : 'Menü öffnen'}</span>
            </button>
          </div>
        </div>
        {open && (
          <nav id="admin-mobile-nav" aria-label="Admin-Navigation" className="border-t border-brand-cream/15 lg:hidden">
            <ul className="px-4 py-2">
              {NAV.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) => `flex items-center gap-2 rounded-sm px-3 py-3.5 font-semibold ${isActive ? 'bg-brand-cream/10 text-white' : 'text-brand-cream/85'}`}
                    data-testid={`admin-mnav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <item.icon className="h-4 w-4" aria-hidden="true" /> {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </header>
      <div className="flex">
        <nav aria-label="Admin-Navigation" className="sticky top-[61px] hidden h-[calc(100vh-61px)] w-56 shrink-0 border-r border-brand-line bg-brand-cream lg:block">
          <ul className="space-y-1 p-4">
            {NAV.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) => `flex items-center gap-2 rounded-sm px-3 py-2.5 text-sm font-semibold transition-colors ${isActive ? 'bg-brand-olive text-white' : 'text-brand-brown hover:bg-brand-beige'}`}
                  data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <item.icon className="h-4 w-4" aria-hidden="true" /> {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <main id="admin-main" tabIndex={-1} className="min-w-0 flex-1 p-4 outline-none sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
